import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError } from "../../../lib/api";
import {
  consoleApi,
  type BookingPromotionRedemption,
} from "../../../lib/admin-console-api";
import { useAuth } from "../../../lib/auth";
import { useAppPreferences, type TranslationValues } from "../../../lib/preferences";
import { useConfirm } from "../../../components/ConfirmDialog";
import {
  errorMessage,
  LoadState,
  Notice,
  PageHeader,
  useLoad,
} from "../ui";
import { PromotionCampaignForm } from "../promotions/PromotionCampaignForm";
import { PromotionDetail } from "../promotions/PromotionDetail";
import { PromotionList } from "../promotions/PromotionList";
import { toPromotionRedemptionRow } from "../promotions/promotionAdapters";
import {
  emptyPromotionConfiguration,
  type PromotionRecord,
  type PromotionRedemptionRow,
  type PromotionVenueOption,
} from "../promotions/types";

const REDEMPTION_PAGE_SIZE = 50;

/** Deep link to a booking through the existing activity feed (no booking page exists). */
export function promotionBookingHref(bookingId: string): string {
  return `/admin/activity?kind=booking&q=${encodeURIComponent(bookingId)}`;
}

function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === "admin";
}

function isRevisionStaleError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    /revision changed/i.test(error.detail)
  );
}

/**
 * Campaign-page transient notices, stored as semantic keys and translated at
 * render so a normal EN/AR preference change re-localizes them instead of
 * freezing the language they were created in. Server details are dynamic
 * text: shown as-is when present, otherwise the translated fallback key.
 * Bounded to these pages — the shared useNotices hook is untouched.
 */
interface NoticeContent {
  key: string;
  values?: TranslationValues;
  detail?: string;
}

function usePromotionNotices() {
  const { t } = useAppPreferences();
  const [notice, setNoticeKey] = useState<NoticeContent | null>(null);
  const [error, setErrorKey] = useState<NoticeContent | null>(null);
  const banners = (
    <>
      <Notice
        message={notice ? t(notice.key, notice.values) : null}
        onDismiss={() => setNoticeKey(null)}
        tone="success"
      />
      <Notice
        message={error ? (error.detail ?? t(error.key, error.values)) : null}
        onDismiss={() => setErrorKey(null)}
        tone="danger"
      />
    </>
  );
  return { banners, setErrorKey, setNoticeKey };
}

/** Key-based error: server detail when the server spoke, else a translated fallback. */
function promotionErrorKey(error: unknown, fallbackKey: string): NoticeContent {
  if (error instanceof ApiError) return { detail: error.detail, key: fallbackKey };
  return { key: fallbackKey };
}

function useVenueOptions(): {
  options: PromotionVenueOption[];
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const venues = useLoad(
    () => consoleApi.listVenues().then((response) => response.venues),
    [],
  );
  const options = useMemo<PromotionVenueOption[]>(
    () => (venues.data ?? []).map((venue) => ({ id: venue.id, name: venue.name })),
    [venues.data],
  );
  return {
    options,
    error: venues.error,
    loading: venues.loading,
    reload: () => void venues.reload(),
  };
}

function VenueLoadWarning({ onRetry }: { onRetry: () => void }) {
  const { t } = useAppPreferences();
  return (
    <div className="ops-banner" data-tone="warning" role="alert">
      <span className="min-w-0 flex-1">
        {t("Venues could not be loaded. You can still save; an empty selection applies everywhere.")}
      </span>
      <button className="ops-button" data-size="sm" data-variant="secondary" onClick={onRetry} type="button">
        {t("Retry")}
      </button>
    </div>
  );
}

function CampaignErrorBanner({ error }: { error: NoticeContent | null }) {
  const { t } = useAppPreferences();
  if (!error) return null;
  return (
    <div className="ops-banner" data-tone="danger" role="alert">
      <span className="min-w-0 flex-1">{error.detail ?? t(error.key, error.values)}</span>
    </div>
  );
}

function useToggleActive(onChanged: (record: PromotionRecord) => void) {
  const { t } = useAppPreferences();
  const { banners, setErrorKey, setNoticeKey } = usePromotionNotices();
  const { confirm, dialog } = useConfirm();
  const [busyId, setBusyId] = useState<string | null>(null);

  const toggle = async (record: PromotionRecord) => {
    const pausing = record.is_active;
    const ok = await confirm({
      title: pausing ? t("Pause this campaign?") : t("Resume this campaign?"),
      description: pausing
        ? t("New bookings stop receiving it. Accepted holds keep the terms they were given.")
        : t("New bookings can receive it again. Past bookings are unchanged."),
      confirmButtonLabel: pausing ? t("Pause") : t("Resume"),
      cancelButtonLabel: t("Cancel"),
    });
    if (!ok) return;
    setBusyId(record.id);
    setErrorKey(null);
    setNoticeKey(null);
    try {
      const updated = await consoleApi.setPromotionActive(record.id, !pausing);
      onChanged(updated);
      setNoticeKey({
        key: pausing
          ? "Campaign paused. It no longer applies to new bookings."
          : "Campaign resumed. New bookings can receive it again.",
      });
    } catch (error) {
      setErrorKey(
        promotionErrorKey(
          error,
          pausing ? "The campaign was not paused." : "The campaign was not resumed.",
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  return { banners, busyId, dialog, toggle };
}

export function PromotionsListPage() {
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const canEdit = useIsAdmin();
  const promotions = useLoad(
    () => consoleApi.listPromotions().then((response) => response.promotions),
    [],
  );
  const toggleActive = useToggleActive((updated) => {
    promotions.setData((current) =>
      (current ?? []).map((record) => (record.id === updated.id ? updated : record)),
    );
  });

  return (
    <div className="console-page">
      {toggleActive.dialog}
      <PageHeader
        description={t("Discount campaigns for bookings. Pausing affects new bookings only.")}
        kicker={t("Platform")}
        title={t("Booking campaigns")}
      />
      {toggleActive.banners}
      <PromotionList
        canEdit={canEdit}
        error={promotions.error}
        loading={promotions.loading}
        onCreate={canEdit ? () => navigate("/admin/promotions/new") : undefined}
        onPause={canEdit ? (record) => void toggleActive.toggle(record) : undefined}
        onResume={canEdit ? (record) => void toggleActive.toggle(record) : undefined}
        onRetry={() => void promotions.reload()}
        onSelect={(record) => navigate(`/admin/promotions/${record.id}`)}
        promotions={promotions.data ?? []}
      />
      {toggleActive.busyId ? (
        <p className="console-muted" role="status">{t("Saving...")}</p>
      ) : null}
    </div>
  );
}

function useRedemptions(promotionId: string, resetKey: string) {
  const { t } = useAppPreferences();
  const [items, setItems] = useState<BookingPromotionRedemption[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setCursor(null);
    setError(null);
    setMoreError(null);
    setLoading(true);
    consoleApi
      .listRedemptions(promotionId, { limit: REDEMPTION_PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setItems(response.redemptions);
        setCursor(response.next_cursor);
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        setError(errorMessage(loadError, t("Redemptions could not be loaded.")));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionId, resetKey, generation]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const response = await consoleApi.listRedemptions(promotionId, {
        cursor,
        limit: REDEMPTION_PAGE_SIZE,
      });
      setItems((current) => [...current, ...response.redemptions]);
      setCursor(response.next_cursor);
    } catch (loadError) {
      setMoreError(errorMessage(loadError, t("More redemptions could not be loaded.")));
    } finally {
      setLoadingMore(false);
    }
  };

  const rows: PromotionRedemptionRow[] = useMemo(
    () => items.map(toPromotionRedemptionRow),
    [items],
  );

  return {
    rows,
    loading,
    loadingMore,
    error,
    moreError,
    hasMore: cursor !== null,
    loadMore: () => void loadMore(),
    reload: () => setGeneration((value) => value + 1),
  };
}

export function PromotionDetailPage() {
  const { promotionId = "" } = useParams();
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const canEdit = useIsAdmin();
  const [revisionReset, setRevisionReset] = useState(0);
  const promotion = useLoad(() => consoleApi.getPromotion(promotionId), [promotionId]);
  const redemptions = useRedemptions(promotionId, String(revisionReset));
  const venues = useVenueOptions();
  const venueNames = useMemo<Record<string, string>>(
    () => Object.fromEntries(venues.options.map((venue) => [venue.id, venue.name])),
    [venues.options],
  );
  const toggleActive = useToggleActive((updated) => {
    promotion.setData(updated);
    setRevisionReset((value) => value + 1);
  });

  const record = promotion.data;

  return (
    <div className="console-page">
      {toggleActive.dialog}
      <PageHeader
        description={null}
        kicker={<Link className="console-link" to="/admin/promotions">{t("Booking campaigns")}</Link>}
        title={record ? record.current_revision.configuration.name_en : t("Campaign")}
      />
      {toggleActive.banners}
      <LoadState
        error={promotion.error}
        isEmpty={!record}
        loading={promotion.loading}
        onRetry={() => void promotion.reload()}
      >
        {record ? (
          <PromotionDetail
            canEdit={canEdit}
            getBookingHref={promotionBookingHref}
            onPause={canEdit ? (item) => void toggleActive.toggle(item) : undefined}
            onResume={canEdit ? (item) => void toggleActive.toggle(item) : undefined}
            onRevise={canEdit ? (item) => navigate(`/admin/promotions/${item.id}/revise`) : undefined}
            onRetryRedemptions={redemptions.reload}
            record={record}
            redemptions={redemptions.rows}
            redemptionsError={redemptions.error}
            redemptionsLoading={redemptions.loading}
            venueNames={venueNames}
          />
        ) : null}
      </LoadState>
      {!redemptions.loading && !redemptions.error && redemptions.hasMore && record ? (
        <div className="console-button-row">
          <button
            className="ops-button"
            data-size="sm"
            data-variant="secondary"
            disabled={redemptions.loadingMore}
            onClick={redemptions.loadMore}
            type="button"
          >
            {redemptions.loadingMore ? t("Loading...") : t("Load more")}
          </button>
        </div>
      ) : null}
      {redemptions.moreError ? (
        <p className="console-error" role="alert">{redemptions.moreError}</p>
      ) : null}
    </div>
  );
}

export function PromotionCreatePage() {
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const canEdit = useIsAdmin();
  const venues = useVenueOptions();
  const [busy, setBusy] = useState(false);
  const [error, setErrorKey] = useState<NoticeContent | null>(null);
  // A fresh object every render would reset the form on each parent update
  // (venues finishing, notices), wiping what was typed. Keep one blank.
  const blank = useMemo(emptyPromotionConfiguration, []);

  const submit = async (payload: PromotionRecord["current_revision"]["configuration"]) => {
    if (!canEdit || busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      const created = await consoleApi.createPromotion(payload);
      navigate(`/admin/promotions/${created.id}`);
    } catch (submitError) {
      setErrorKey(promotionErrorKey(submitError, "The campaign was not created."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="console-page">
      <PageHeader
        description={t("New terms apply from the start moment. Set a code only for code offers.")}
        kicker={<Link className="console-link" to="/admin/promotions">{t("Booking campaigns")}</Link>}
        title={t("New campaign")}
      />
      {venues.error ? <VenueLoadWarning onRetry={venues.reload} /> : null}
      <CampaignErrorBanner error={error} />
      <PromotionCampaignForm
        busy={busy}
        canEdit={canEdit}
        initial={blank}
        onCancel={() => navigate("/admin/promotions")}
        onSubmit={(payload) => void submit(payload)}
        submitLabel={t("Create campaign")}
        venueOptions={venues.options}
      />
    </div>
  );
}

export function PromotionRevisePage() {
  const { promotionId = "" } = useParams();
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const canEdit = useIsAdmin();
  const venues = useVenueOptions();
  const promotion = useLoad(() => consoleApi.getPromotion(promotionId), [promotionId]);
  const [busy, setBusy] = useState(false);
  const [error, setErrorKey] = useState<NoticeContent | null>(null);
  const [stale, setStale] = useState<{
    latestRevisionId: string;
    latestRevisionNumber: number;
  } | null>(null);

  const record = promotion.data;
  // The draft source stays fixed while this page is open so a stale reload
  // never silently replaces what was typed; only the expected revision
  // pointer moves forward after an explicit second submit.
  const [draftSource, setDraftSource] = useState<{
    promotionId: string;
    configuration: PromotionRecord["current_revision"]["configuration"];
  } | null>(null);
  useEffect(() => {
    if (record && (draftSource === null || draftSource.promotionId !== promotionId)) {
      setDraftSource({ promotionId, configuration: record.current_revision.configuration });
    }
  }, [record, draftSource, promotionId]);
  const expectedRevisionId =
    stale?.latestRevisionId ?? record?.current_revision.id ?? null;

  const submit = async (payload: PromotionRecord["current_revision"]["configuration"]) => {
    if (!canEdit || busy || !expectedRevisionId) return;
    setBusy(true);
    setErrorKey(null);
    try {
      const updated = await consoleApi.revisePromotion(promotionId, {
        ...payload,
        expected_revision_id: expectedRevisionId,
      });
      navigate(`/admin/promotions/${updated.id}`);
    } catch (submitError) {
      if (isRevisionStaleError(submitError)) {
        try {
          const latest = await consoleApi.getPromotion(promotionId);
          promotion.setData(latest);
          setStale({
            latestRevisionId: latest.current_revision.id,
            latestRevisionNumber: latest.current_revision.revision_number,
          });
          setErrorKey({
            key: "This campaign changed while you were editing (now revision {revision}). Your entries are kept below. Review the latest terms, then submit again to confirm.",
            values: { revision: latest.current_revision.revision_number },
          });
        } catch {
          setErrorKey(promotionErrorKey(submitError, "The campaign was not revised."));
        }
      } else {
        setErrorKey(promotionErrorKey(submitError, "The campaign was not revised."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="console-page">
      <PageHeader
        description={t("Revisions never rewrite history. Accepted holds keep the terms they were given.")}
        kicker={<Link className="console-link" to={`/admin/promotions/${promotionId}`}>{t("Campaign")}</Link>}
        title={t("Revise terms")}
      />
      {venues.error ? <VenueLoadWarning onRetry={venues.reload} /> : null}
      <CampaignErrorBanner error={error} />
      <LoadState
        error={promotion.error}
        isEmpty={!record || !draftSource}
        loading={promotion.loading}
        onRetry={() => void promotion.reload()}
      >
        {record && draftSource ? (
          <PromotionCampaignForm
            busy={busy}
            canEdit={canEdit}
            expectedRevisionId={expectedRevisionId}
            initial={draftSource.configuration}
            onCancel={() => navigate(`/admin/promotions/${promotionId}`)}
            onSubmit={(payload) => void submit(payload)}
            submitLabel={t("Save new revision")}
            venueOptions={venues.options}
          />
        ) : null}
      </LoadState>
    </div>
  );
}
